"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { PendulumDiagram } from "@/components/tools/physics-fields/pendulum-diagram";
import {
  DiagramFrame,
  QuantityField,
  type SigChoice,
  SigFigsField,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { BODIES } from "@/lib/tool-kit/physics-fields/bodies";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readPlain, readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  amplitudeFactor,
  type PendulumError,
  type PendulumTarget,
  seriesFactor,
  solvePendulum,
} from "@/lib/tool-kit/physics-fields/pendulum";
import { pendulumWorking } from "@/lib/tool-kit/physics-fields/pendulum-working";
import {
  ACCELERATION_UNITS,
  fromBase,
  LENGTH_UNITS,
  TIME_UNITS,
  type UnitDef,
} from "@/lib/tool-kit/physics-fields/units";

/** English strings. A translated content file overrides these through `ui`. */
export const PENDULUM_PERIOD_CALCULATOR_STRINGS = {
  solveFor: "Solve for",
  period: "Period T",
  length: "Length L (pivot to the centre of the bob)",
  lengthShort: "Length L",
  gravity: "Gravity g",
  planetLabel: "Set g for",
  custom: "Custom value",
  planetNames: {
    sun: "Sun",
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth (standard gravity)",
    moon: "Moon",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  amplitude: "Amplitude θ₀ (release angle, degrees)",
  amplitudeHint:
    "The angle from the vertical at which the bob is let go, between 0° and 180°.",
  correct: "Correct for the amplitude (large-angle formula)",
  unitFor: "Unit for {name}",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  periodCard: "Period T",
  frequency: "Frequency f = 1/T",
  smallAngle: "Small-angle period T₀",
  correction: "Amplitude correction T/T₀",
  maxSpeed: "Speed at the bottom",
  perMinute: "Complete swings per minute",
  working: "The working",
  tableHeading: "How amplitude changes the period",
  tableCaption:
    "The exact period compared with the small-angle formula, for the same L and g.",
  colAmplitude: "Amplitude θ₀",
  colFactor: "T / T₀",
  colLonger: "Longer than T₀ by",
  colSeries: "Series 1 + θ₀²/16",
  diagramCaption:
    "The bob is released at θ₀ and swings to the same angle on the other side; the lighter copy shows where it ends up.",
  diagramLabel: "A simple pendulum at its release angle",
  diagramDescription:
    "A string of length L hanging from a fixed pivot at the top. A bob on the right is raised to angle theta-zero from the vertical dashed line, and a faint copy of the bob sits at the mirror position on the left.",
  errorMissing:
    "Fill in every box except the one you are solving for, and the amplitude when the correction is on.",
  errorPositive: "Length, gravity and period must be greater than zero.",
  errorAmplitude:
    "The amplitude must be an angle between 0° and 180°, not including either end.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Assumes a point-mass bob on a massless, inextensible string, swinging in one plane with no air resistance. For a real bob of finite size, use the distance from the pivot to its centre of mass and expect a slightly longer period.",
} as const;

type Key = "L" | "g" | "T";

const KEYS: Record<Key, { target: PendulumTarget; units: readonly UnitDef[] }> =
  {
    L: { target: "length", units: LENGTH_UNITS },
    g: { target: "gravity", units: ACCELERATION_UNITS },
    T: { target: "period", units: TIME_UNITS },
  };

const TABLE_AMPLITUDES = [5, 10, 15, 20, 30, 45, 60, 90] as const;

function typed(value: number, sig: number): string {
  return Number(value.toPrecision(sig)).toExponential().replace("e+", "e");
}

function Failure({
  error,
  strings,
}: {
  error: PendulumError;
  strings: typeof PENDULUM_PERIOD_CALCULATOR_STRINGS;
}) {
  const messages: Record<PendulumError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "amplitude-range": strings.errorAmplitude,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

export default function PendulumPeriodCalculatorWidget() {
  const strings = useToolUi(PENDULUM_PERIOD_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);
  const [target, setTarget] = useState<PendulumTarget>("period");
  const [text, setText] = useState<Record<Key, string>>({
    L: "1",
    g: "9.80665",
    T: "",
  });
  const [unit, setUnit] = useState<Record<Key, string>>({
    L: "m",
    g: "m/s2",
    T: "s",
  });
  const [planet, setPlanet] = useState("earth");
  const [amplitude, setAmplitude] = useState("10");
  const [correct, setCorrect] = useState(true);

  const read = (key: Key) =>
    readQuantity(text[key], KEYS[key].units, unit[key]);
  const outcome = solvePendulum(
    {
      length: read("L"),
      gravity: read("g"),
      period: read("T"),
      amplitudeDegrees: readPlain(amplitude),
      correct,
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const solvedSI: Record<Key, number | null> = {
    L: value?.length ?? null,
    g: value?.gravity ?? null,
    T: value?.period ?? null,
  };

  const changeTarget = (next: PendulumTarget) => {
    const previous = (Object.keys(KEYS) as Key[]).find(
      (key) => KEYS[key].target === target
    );
    const si = previous ? solvedSI[previous] : null;
    if (previous && si !== null) {
      setText({
        ...text,
        [previous]: typed(
          fromBase(KEYS[previous].units, unit[previous], si),
          sig
        ),
      });
    }
    setTarget(next);
  };
  const pickPlanet = (id: string) => {
    setPlanet(id);
    const body = BODIES.find((b) => b.id === id);
    if (body) {
      setText((c) => ({ ...c, g: String(body.gravity) }));
      setUnit((c) => ({ ...c, g: "m/s2" }));
    }
  };
  const field = (key: Key, label: string, hint?: string) => {
    const spec = KEYS[key];
    const isSolved = spec.target === target;
    const si = solvedSI[key];
    let shown = text[key];
    if (isSolved) {
      shown =
        si === null
          ? ""
          : formatSig(fmt, fromBase(spec.units, unit[key], si), sig);
    }
    return (
      <QuantityField
        hint={hint}
        id={`pp-${key}`}
        label={label}
        onChange={(x) => {
          if (key === "g") {
            setPlanet("custom");
          }
          setText({ ...text, [key]: x });
        }}
        onUnit={(x) => setUnit({ ...unit, [key]: x })}
        solved={isSolved}
        unit={unit[key]}
        unitLabel={t(strings.unitFor, { name: label })}
        units={spec.units}
        value={shown}
      />
    );
  };
  const planetNames = strings.planetNames as Record<string, string>;
  const tableRows = TABLE_AMPLITUDES.map((degrees) => {
    const radians = (degrees * Math.PI) / 180;
    const factor = amplitudeFactor(radians);
    return [
      `${fmt(degrees)}°`,
      fmt(factor, { minimumFractionDigits: 4, maximumFractionDigits: 4 }),
      `${fmt((factor - 1) * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
      fmt(seriesFactor(radians, 2), {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }),
    ];
  });

  return (
    <>
      <PillGroup
        label={strings.solveFor}
        name="pp-solve"
        onChange={changeTarget}
        options={[
          { value: "period", label: strings.period },
          { value: "length", label: strings.lengthShort },
          { value: "gravity", label: strings.gravity },
        ]}
        value={target}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {field("L", strings.length)}
        {field("g", strings.gravity)}
        {field("T", strings.period)}
        <SelectField
          id="pp-planet"
          label={strings.planetLabel}
          onChange={pickPlanet}
          options={[
            { value: "custom", label: strings.custom },
            ...BODIES.map((b) => ({
              value: b.id,
              label: planetNames[b.id] ?? b.id,
            })),
          ]}
          value={planet}
        />
        <TextField
          hint={strings.amplitudeHint}
          id="pp-amplitude"
          label={strings.amplitude}
          numeric
          onChange={setAmplitude}
          value={amplitude}
        />
      </div>
      <Checkbox
        checked={correct}
        label={strings.correct}
        onChange={setCorrect}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.periodCard}
                value={`${formatSig(fmt, value.period, sig)} s`}
              />
              <StatCard
                label={strings.frequency}
                value={`${formatSig(fmt, value.frequency, sig)} Hz`}
              />
              <StatCard
                label={strings.perMinute}
                value={formatSig(fmt, value.frequency * 60, sig)}
              />
              <StatCard
                label={strings.smallAngle}
                value={`${formatSig(fmt, value.smallAnglePeriod, sig)} s`}
              />
              <StatCard
                label={strings.correction}
                value={fmt(value.factor, {
                  minimumFractionDigits: 5,
                  maximumFractionDigits: 5,
                })}
              />
              <StatCard
                label={strings.maxSpeed}
                value={
                  value.maxSpeed === null
                    ? "—"
                    : `${formatSig(fmt, value.maxSpeed, sig)} m/s`
                }
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={pendulumWorking(target, value, correct, fmt, sig)}
            />
            {value.amplitudeDegrees === null ? null : (
              <DiagramFrame caption={strings.diagramCaption}>
                <PendulumDiagram
                  degrees={value.amplitudeDegrees}
                  description={strings.diagramDescription}
                  id="pp-diagram"
                  label={strings.diagramLabel}
                />
              </DiagramFrame>
            )}
          </>
        )}
      </section>
      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable
          caption={strings.tableCaption}
          head={[
            strings.colAmplitude,
            strings.colFactor,
            strings.colLonger,
            strings.colSeries,
          ]}
          rows={tableRows}
        />
      </section>
      <SigFigsField
        format={fmt}
        id="pp-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
